import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { InvoiceAPI } from "../lib/api";
import { formatCurrency, formatDate, STATUS_STYLES } from "../lib/format";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import {
  ArrowUpRight,
  CurrencyGbp,
  FileText,
  Hourglass,
  CheckCircle,
  Plus,
} from "@phosphor-icons/react";

const StatCard = ({ label, value, icon: Icon, accent = false, testid }) => (
  <div
    className={`p-4 sm:p-6 border border-border bg-white relative group hover:-translate-y-0.5 transition-transform ${
      accent ? "lg:row-span-1" : ""
    }`}
    data-testid={testid}
  >
    <div className="flex items-start justify-between">
      <div className="overline text-muted-foreground">{label}</div>
      <div className="text-primary opacity-70 group-hover:opacity-100 transition-opacity">
        <Icon size={20} weight="bold" />
      </div>
    </div>
    <div className="mt-4 sm:mt-6 font-display text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter">
      {value}
    </div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([InvoiceAPI.stats(), InvoiceAPI.list()])
      .then(([s, list]) => {
        setStats(s);
        setRecent(list.slice(0, 5));
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        overline="Overview"
        title="Dashboard"
        description="Real-time snapshot of your invoicing pipeline."
        actions={
          <Link to="/invoices/new">
            <Button data-testid="dashboard-new-invoice-btn" className="rounded-sm">
              <Plus size={16} weight="bold" className="mr-2" />
              New Invoice
            </Button>
          </Link>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-8 lg:mb-10">
        <StatCard
          label="Total Invoices"
          value={loading ? "—" : stats?.total_count ?? 0}
          icon={FileText}
          testid="stat-total-invoices"
        />
        <StatCard
          label="Revenue (Paid)"
          value={loading ? "—" : formatCurrency(stats?.total_revenue ?? 0)}
          icon={CurrencyGbp}
          testid="stat-revenue"
        />
        <StatCard
          label="Outstanding"
          value={loading ? "—" : formatCurrency(stats?.outstanding ?? 0)}
          icon={Hourglass}
          testid="stat-outstanding"
        />
        <StatCard
          label="Paid Count"
          value={loading ? "—" : stats?.by_status?.Paid ?? 0}
          icon={CheckCircle}
          testid="stat-paid-count"
        />
      </div>

      {/* Recent invoices */}
      <div className="border border-border bg-white">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <div className="overline text-muted-foreground">Recent</div>
            <h2 className="font-display text-xl font-bold tracking-tight mt-1">
              Latest Invoices
            </h2>
          </div>
          <Link
            to="/invoices"
            className="text-sm text-primary hover:underline flex items-center gap-1"
            data-testid="view-all-invoices-link"
          >
            View all <ArrowUpRight size={14} weight="bold" />
          </Link>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            Loading…
          </div>
        ) : recent.length === 0 ? (
          <div className="p-10 text-center">
            <div className="text-sm text-muted-foreground mb-4">
              No invoices yet. Create your first one.
            </div>
            <Link to="/invoices/new">
              <Button data-testid="empty-create-invoice-btn" className="rounded-sm">
                <Plus size={16} weight="bold" className="mr-2" />
                New Invoice
              </Button>
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {recent.map((inv) => (
              <Link
                key={inv.id}
                to={`/invoices/${inv.id}`}
                className="flex flex-col sm:grid sm:grid-cols-12 gap-2 sm:gap-4 px-4 sm:px-6 py-4 hover:bg-muted/50 transition-colors"
                data-testid={`recent-invoice-${inv.invoice_number}`}
              >
                <div className="sm:col-span-3 font-mono text-sm font-semibold flex items-center justify-between">
                  <span>{inv.invoice_number}</span>
                  <span
                    className={`sm:hidden text-xs font-semibold px-2 py-0.5 border ${
                      STATUS_STYLES[inv.status] || ""
                    }`}
                  >
                    {inv.status}
                  </span>
                </div>
                <div className="sm:col-span-4 text-sm truncate">
                  {inv.client_name}
                </div>
                <div className="sm:col-span-2 text-xs text-muted-foreground">
                  {formatDate(inv.issue_date)}
                </div>
                <div className="hidden sm:block sm:col-span-2">
                  <span
                    className={`inline-block text-xs font-semibold px-2 py-0.5 border ${
                      STATUS_STYLES[inv.status] || ""
                    }`}
                  >
                    {inv.status}
                  </span>
                </div>
                <div className="sm:col-span-1 text-left sm:text-right font-mono text-sm font-semibold">
                  {formatCurrency(inv.total, inv.currency)}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
